import {
  applyToViewer,
  EXPOSURE_RANGE,
  loadExposureSettings,
  METERS,
  AE_RANGE,
  saveExposureSettings,
  SPEEDS,
  STRENGTH_RANGE,
  passThroughQuery,
  viewerQuery,
  walkbakeOf,
  withUrlOverrides,
  type ExposureSettings,
} from "../walk/exposureSettings";
import {
  applyEffectsToViewer,
  bloomHalfFor,
  EFFECT_RANGE,
  OUTDOOR_RANGE,
  effectQuery,
  maxPixelRatioFor,
  loadEffectSettings,
  saveEffectSettings,
  setMotionBlurStrength,
  toggleMotionBlur,
  viewerPatch,
  withEffectUrlOverrides,
  effectiveMotionBlur,
  type EffectSettings,
  type ViewerEffects,
} from "../walk/effectSettings";
import {
  applyLookToViewer,
  DEFAULT_LOOK_SETTINGS,
  loadLookSettings,
  lookQuery,
  lookViewerPatch,
  saveLookSettings,
  SENSITIVITY_RANGE,
  withLookUrlOverrides,
  ZOOM_RANGE,
  type LookSettings,
} from "../walk/lookSettings";
import {
  applyPlantLightToViewer,
  DEFAULT_PLANT_LIGHT_SETTINGS,
  loadPlantLightSettings,
  PLANT_LIGHT_RANGES,
  plantLightQuery,
  savePlantLightSettings,
  withPlantLightUrlOverrides,
  type PlantLightNumber,
  type PlantLightSettings,
} from "../walk/plantLightSettings";
import { commitTyped, formatValue, type NumericRange } from "../walk/numeric";
import { slideWithUrlOverride } from "../walk/walkSettings";
import { installInputTweaks } from "../walk/inputTweaks";

import {
  DEFAULT_KIOSK_SETTINGS,
  loadKioskSettings,
  saveKioskSettings,
  withKioskUrlOverrides,
  type KioskSettings,
} from "../walk/kioskSettings";
import {
  enterFullscreen,
  exitFullscreen,
  FULLSCREEN_EVENTS,
  fullscreenElementOf,
  isFullscreen,
  isInstalledApp,
  showFullscreenButton,
} from "../walk/fullscreen";
import { judgeLoad, type ViewerProbe } from "../walk/loadWatch";
import { ScreenWakeLock } from "../walk/wakeLock";
import { diagFrameOpened } from "../diag/state";
import { applyToViewerQuery, effectiveSearch } from "../walk/advanced";

/** Long-press on the discreet kiosk control to leave kiosk mode, and the idle time before the mouse cursor hides. */
const KIOSK_LEAVE_MS = 800;
const CURSOR_IDLE_MS = 3000;

const ICON_ENTER = `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
// Motion blur button: a dot with speed lines.
const ICON_BLUR = `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><circle cx="16" cy="12" r="4" fill="currentColor"/><path d="M3 8h7M2 12h8M3 16h7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

const ICON_EXIT = `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** What the frame shows: see ViewerProbe. A cross-origin document (Chrome's error page) throws on access. */
function probeViewer(frame: HTMLIFrameElement): ViewerProbe {
  try {
    const win = frame.contentWindow as (Window & { walkbake?: { ready?: boolean; error?: unknown } }) | null;
    const state = win?.walkbake;
    if (state) return state.ready || state.error ? "settled" : "running";
    return win?.document.getElementById("walk") ? "starting" : "absent";
  } catch {
    return "absent";
  }
}

export interface PlayerScreenCallbacks {
  onExit(): void;
  /** The viewer finished loading (ready) or reported its own error: the load did not run out of memory. */
  onViewerSettled(): void;
  /** The ⚙ panel's Diagnostics button. */
  onDiagnostics(): void;
  /** The ⚙ panel's Advanced / Debug button. */
  onAdvanced(): void;
  /** The viewer iframe never started (error page, or no viewer script): main.ts goes back home with an error. */
  onViewerFailed(): void;
}

function options(values: (string | number)[], label: (value: string | number) => string): string {
  return values.map((value) => `<option value="${value}">${label(value)}</option>`).join("");
}

const EFFECT_SLIDERS: [keyof ViewerEffects, string, NumericRange][] = [
  ["glow", "Glow", EFFECT_RANGE],
  ["outdoor", "Outdoor exposure", OUTDOOR_RANGE],
  ["motionBlur", "Motion blur", EFFECT_RANGE],
];

/** A labelled slider with a typed-value box beside it (`data-num` ties the pair together). */
function numberRow(id: string, label: string, range: NumericRange): string {
  return `<div class="exposure-row">
    <span>${label}</span>
    <input type="range" data-num="${id}" min="${range.min}" max="${range.max}" step="${range.step}" aria-label="${label}" />
    <input type="text" class="num-input" data-num-text="${id}" inputmode="decimal" autocomplete="off" aria-label="${label}, type a value" />
  </div>`;
}

const LOOK_SLIDERS: [keyof LookSettings, string, NumericRange][] = [
  ["sensitivity", "Look sensitivity", SENSITIVITY_RANGE],
  ["zoom", "Sensitivity follows zoom", ZOOM_RANGE],
];

const PLANT_SLIDERS: [PlantLightNumber, string][] = [
  ["vegetationLight", "Vegetation light"],
  ["leafShine", "Leaf shine"],
  ["leafDetail", "Leaf detail"],
  ["leafHighlights", "Highlight softening"],
  ["leafTranslucency", "Leaf translucency"],
  ["sunGlow", "Sun glow"],
];

const LOOK_HINT = `<p class="exposure-auto">Touch and mouse. At 1 a long lens turns less per pixel and a wide one more; at 0 it is fixed.</p>`;

const OUTDOOR_HINT = `<p class="exposure-auto">Stops; only what is seen through the windows. Glow is separate.</p>`;

const EXPOSURE_PANEL = `
  <h2>Exposure</h2>
  <label><input type="checkbox" data-exposure="auto" /> Auto exposure</label>
  ${numberRow("exposure", "Exposure", EXPOSURE_RANGE)}
  <p class="exposure-auto">Auto value: <output data-exposure-out="auto">n/a</output></p>
  <h2>Light</h2>
  <label><input type="checkbox" data-plant-lights /> Light highlights</label>
  ${numberRow("lightIntensity", "Highlights", PLANT_LIGHT_RANGES.lightIntensity)}
  <h2>Vegetation</h2>
  ${PLANT_SLIDERS.map(([key, label]) => numberRow(key, label, PLANT_LIGHT_RANGES[key])).join("")}
  <button type="button" class="look-reset" data-plant-reset>Reset light and vegetation</button>
  <h2>Effects</h2>
  ${EFFECT_SLIDERS.map(([key, label, range]) => numberRow(key, label, range) + (key === "outdoor" ? OUTDOOR_HINT : "")).join("")}
  <h2>Look</h2>
  ${LOOK_SLIDERS.map(([key, label, range]) => numberRow(key, label, range)).join("")}
  ${LOOK_HINT}
  <button type="button" class="look-reset" data-look-reset>Reset look</button>
  <h3>Test options <small>(temporary)</small></h3>
  ${numberRow("strength", "Strength", STRENGTH_RANGE)}
  ${numberRow("range", "Range (stops)", AE_RANGE)}
  <label class="exposure-row">Speed
    <select data-exposure="speed">${options(SPEEDS, (v) => String(v).replace(/^./, (c) => c.toUpperCase()))}</select></label>
  <label class="exposure-row">Metering
    <select data-exposure="meter">${options(METERS, (v) => ({ centre: "Centre-weighted", average: "Average", highlights: "Ignore highlights" })[v as "centre"])}</select></label>
`;

/**
 * Full-screen frame running the Walk Bake viewer the Player ships (public/viewer/),
 * loaded from the service worker, which serves the walk's data out of the open ZIP.
 * The only Player UI on top is a close button, a touch-settings button, an
 * exposure button and a fullscreen button (top right, clear of the viewer's debug panel).
 * In kiosk mode these give way to one discreet press-and-hold control (see walk/kioskSettings.ts).
 */
/**
 * `window.player.settings` for tests: read-only getters on the current effect values. Merged into an
 * existing `window.player` rather than replacing it.
 */
function exposeSettings(read: () => EffectSettings & LookSettings & Pick<ExposureSettings, "exposure" | "strength" | "range">): void {
  const host = window as unknown as { player?: Record<string, unknown> };
  const player = host.player ?? {};
  const settings = Object.defineProperties({ ...(player.settings as object | undefined) }, {
    glow: { get: () => read().glow, enumerable: true, configurable: true },
    outdoor: { get: () => read().outdoor, enumerable: true, configurable: true },
    motionBlur: { get: () => read().motionBlur, enumerable: true, configurable: true },
    lookSensitivity: { get: () => read().sensitivity, enumerable: true, configurable: true },
    lookZoom: { get: () => read().zoom, enumerable: true, configurable: true },
    exposure: { get: () => read().exposure, enumerable: true, configurable: true },
    aeStrength: { get: () => read().strength, enumerable: true, configurable: true },
    aeRange: { get: () => read().range, enumerable: true, configurable: true },
  });
  host.player = { ...player, settings };
}

export class PlayerScreen {
  readonly element: HTMLElement;
  private frame: HTMLIFrameElement | null = null;
  private uninstallTweaks: (() => void) | null = null;
  private exposure: ExposureSettings = loadExposureSettings();
  private effects: EffectSettings = loadEffectSettings();
  private look: LookSettings = loadLookSettings();
  private plants: PlantLightSettings = loadPlantLightSettings();
  private slide = slideWithUrlOverride(effectiveSearch());
  private kioskSettings: KioskSettings = { ...DEFAULT_KIOSK_SETTINGS };
  /** Read once at startup: `(display-mode: fullscreen)` also matches while the Fullscreen API is active. */
  private readonly installed = isInstalledApp(window);
  private readonly wakeLock = new ScreenWakeLock(navigator, document);
  private readonly fsButton: HTMLButtonElement;
  private readonly blurButton: HTMLButtonElement;
  private cursorTimer = 0;
  private cursorStyle: HTMLStyleElement | null = null;
  private readonly panel: HTMLElement;
  private readonly exposurePanel: HTMLElement;
  private autoValueTimer = 0;
  private loadWatchTimer = 0;

  constructor(private readonly callbacks: PlayerScreenCallbacks) {
    this.element = document.createElement("div");
    this.element.id = "player";
    this.element.hidden = true;
    this.element.innerHTML = `
      <button class="exit-btn" aria-label="Close walk">✕</button>
      <button class="settings-btn" aria-label="Tablet settings" aria-expanded="false">⚙</button>
      <button class="exposure-btn" aria-label="Exposure" aria-expanded="false">☀</button>
      <button class="blur-btn" aria-label="Motion blur" aria-pressed="false" title="Motion blur on/off">${ICON_BLUR}</button>
      <button class="fs-btn" hidden></button>
      <button class="kiosk-exit" aria-label="Leave kiosk mode (press and hold)" title="Press and hold to leave kiosk mode"></button>
      <div class="pinch-readout" aria-live="polite" hidden></div>
      <div class="settings-panel" hidden>
        <h2>Tablet</h2>
        <label><input type="checkbox" data-kiosk="kiosk" /> Kiosk mode (press and hold the dot, top right, to leave)</label>
        <label><input type="checkbox" data-kiosk="fps" /> Show FPS box (key F)</label>
        <button type="button" class="btn diag-open" data-action="diagnostics">Diagnostics</button>
        <button type="button" class="btn diag-open" data-action="advanced">Advanced / Debug…</button>
      </div>
      <div class="settings-panel exposure-panel" hidden>${EXPOSURE_PANEL}</div>
    `;
    this.element.querySelector(".exit-btn")?.addEventListener("click", () => callbacks.onExit());
    this.element.querySelector('[data-action="advanced"]')?.addEventListener("click", () => {
      this.closePanels();
      callbacks.onAdvanced();
    });
    this.element.querySelector('[data-action="diagnostics"]')?.addEventListener("click", () => {
      this.closePanels();
      callbacks.onDiagnostics();
    });

    this.panel = this.element.querySelector(".settings-panel") as HTMLElement;
    const toggle = this.element.querySelector(".settings-btn") as HTMLButtonElement;
    this.exposurePanel = this.element.querySelector(".exposure-panel") as HTMLElement;
    const exposureToggle = this.element.querySelector(".exposure-btn") as HTMLButtonElement;
    // One panel at a time, in the same place.
    const togglePanel = (panel: HTMLElement, button: HTMLButtonElement): void => {
      const open = panel.hidden;
      this.closePanels();
      panel.hidden = !open;
      button.setAttribute("aria-expanded", String(open));
      if (panel === this.exposurePanel) open ? this.startExposurePanel() : this.stopAutoValuePolling();
    };
    toggle.addEventListener("click", () => togglePanel(this.panel, toggle));
    exposureToggle.addEventListener("click", () => togglePanel(this.exposurePanel, exposureToggle));
    this.bindExposurePanel();
    this.bindEffectPanel();
    this.bindLookPanel();
    this.bindPlantPanel();
    this.blurButton = this.element.querySelector(".blur-btn") as HTMLButtonElement;
    this.blurButton.addEventListener("click", () => this.changeEffect(toggleMotionBlur(this.effects)));
    this.syncBlurButton();
    this.fsButton = this.element.querySelector(".fs-btn") as HTMLButtonElement;
    this.bindTablet();
    exposeSettings(() => ({ ...this.effects, motionBlur: effectiveMotionBlur(this.effects), ...this.exposure, ...this.look }));
  }

  private lastShow: { url: string; title: string; gpuMb?: number; preset?: string } | null = null;

  /** Reloads the viewer iframe for the walk that is open (Advanced menu): same walk, settings read again. */
  reloadViewer(): void {
    const last = this.lastShow;
    if (last && !this.element.hidden) this.show(last.url, last.title, last.gpuMb, last.preset);
  }

  /** The kiosk / FPS choices (Advanced menu shows them next to the other switches). */
  tabletSettings(): KioskSettings {
    return { ...this.kioskSettings };
  }

  setTabletOption(key: keyof KioskSettings, on: boolean): void {
    if (key === "kiosk") this.setKiosk(on);
    else this.setFps(on);
    this.syncTabletPanel();
  }

  /** The open walk's window (same origin), or null when no walk is open. For window.walkPlayer. */
  viewerWindow(): Window | null {
    return this.frame?.contentWindow ?? null;
  }

  /** gpuMb: the walk's graphics-memory estimate, shown on the viewer's loading screen and debug panel. */
  show(url: string, title: string, gpuMb?: number, preset?: string): void {
    this.hide();
    const frame = document.createElement("iframe");
    frame.title = title;
    // The viewer reads its auto exposure options from the URL: saved settings, or the Player's own ?ae*= for tests.
    const search = effectiveSearch(); // saved Advanced switches under the real URL (src/walk/advanced.ts)
    this.exposure = withUrlOverrides(loadExposureSettings(), location.search);
    this.slide = slideWithUrlOverride(search);
    this.effects = withEffectUrlOverrides(loadEffectSettings(), location.search);
    this.syncEffectPanel();
    this.look = withLookUrlOverrides(loadLookSettings(), location.search);
    this.syncLookPanel();
    this.plants = withPlantLightUrlOverrides(loadPlantLightSettings(), location.search);
    this.syncPlantPanel();
    const bloomHalf = bloomHalfFor({ preset, gpuMb }, navigator.userAgent, navigator.maxTouchPoints);
    const passed = passThroughQuery(location.search);
    const lowLightLimit = ["tablet", "phone"].includes(preset?.trim().toLowerCase() ?? "") ? "&light_limit=2" : "";
    const query = `${viewerQuery(this.exposure, { gpuMb, slide: this.slide, fps: this.kioskSettings.fps, kiosk: this.kioskSettings.kiosk })}&${effectQuery(this.effects, bloomHalf, maxPixelRatioFor(navigator.userAgent))}&${lookQuery(this.look)}&${plantLightQuery(this.plants)}${lowLightLimit}${passed ? `&${passed}` : ""}`;
    frame.src = `${url}?${applyToViewerQuery(query, search)}`;
    this.lastShow = { url, title, gpuMb, preset };
    frame.allow = "fullscreen";
    diagFrameOpened(frame);
    frame.addEventListener("load", () => {
      if (frame !== this.frame) return; // the about:blank load after hide()
      this.uninstallTweaks?.();
      this.uninstallTweaks = null;
      try {
        // Same origin (served by our worker), so the viewer window is reachable.
        this.uninstallTweaks = installInputTweaks(frame, (value) =>
          this.showPinchReadout("focalLengthMm" in value ? `${Math.round(value.focalLengthMm)} mm` : `${value.zoom.toFixed(1)}×`),
        );
      } catch {
        // Not reachable (e.g. an error page): play without tweaks.
      }
      try {
        this.watchFrameInput(frame.contentWindow as Window);
      } catch {
        // Not reachable: the Player's own listeners still cover the shell.
      }
      frame.contentWindow?.focus();
    });
    this.element.prepend(frame);
    this.frame = frame;
    this.closePanels();
    this.element.hidden = false;
    document.title = title;
    this.renderFullscreen();
    this.updateWakeLock();
    if (this.kioskSettings.kiosk) this.armCursorIdle();
    this.watchLoad(frame);
  }

  /**
   * Polls the viewer until it reports ready or an error of its own (onViewerSettled), or turns out
   * not to have started at all (onViewerFailed, see judgeLoad). A slow load is waited for.
   */
  private watchLoad(frame: HTMLIFrameElement): void {
    this.stopLoadWatch();
    const startedAt = Date.now();
    let loadedAt: number | null = null;
    frame.addEventListener("load", () => {
      loadedAt ??= Date.now();
    });
    this.loadWatchTimer = window.setInterval(() => {
      const now = Date.now();
      const verdict = judgeLoad(probeViewer(frame), now - startedAt, loadedAt === null ? null : now - loadedAt);
      if (verdict === "wait") return;
      this.stopLoadWatch();
      if (verdict === "settled") this.callbacks.onViewerSettled();
      else this.callbacks.onViewerFailed();
    }, 500);
  }

  private stopLoadWatch(): void {
    clearInterval(this.loadWatchTimer);
    this.loadWatchTimer = 0;
  }

  private closePanels(): void {
    this.panel.hidden = true;
    this.exposurePanel.hidden = true;
    for (const button of this.element.querySelectorAll(".settings-btn, .exposure-btn")) {
      button.setAttribute("aria-expanded", "false");
    }
    this.stopAutoValuePolling();
  }

  private exposureInput<T extends HTMLElement>(key: string): T {
    return this.exposurePanel.querySelector(`[data-exposure="${key}"]`) as T;
  }

  private exposureOut(key: string): HTMLOutputElement {
    return this.exposurePanel.querySelector(`[data-exposure-out="${key}"]`) as HTMLOutputElement;
  }

  /** Shows the settings in the panel controls (the open walk may have started with URL overrides). */
  private syncExposurePanel(): void {
    const e = this.exposure;
    this.exposureInput<HTMLInputElement>("auto").checked = e.auto;
    this.showNumber("exposure", EXPOSURE_RANGE, e.exposure);
    this.showNumber("strength", STRENGTH_RANGE, e.strength);
    this.showNumber("range", AE_RANGE, e.range);
    this.exposureInput<HTMLSelectElement>("speed").value = e.speed;
    this.exposureInput<HTMLSelectElement>("meter").value = e.meter;
  }

  private changeExposure(patch: Partial<ExposureSettings>, persist = true): void {
    this.exposure = { ...this.exposure, ...patch };
    if (persist) saveExposureSettings({ ...loadExposureSettings(), ...patch });
    applyToViewer(walkbakeOf(this.frame?.contentWindow), patch);
  }

  private bindExposurePanel(): void {
    this.syncExposurePanel();
    this.exposureInput<HTMLInputElement>("auto").addEventListener("change", (e) =>
      this.changeExposure({ auto: (e.target as HTMLInputElement).checked }));
    const slider = this.numSlider("exposure");
    this.bindNumber("exposure", EXPOSURE_RANGE, () => this.exposure.exposure, (exposure, persist) =>
      this.changeExposure({ exposure }, persist));
    // Double-tap (or double-click) resets to 0. iOS sends no dblclick for a range input, so taps are timed.
    const reset = (): void => {
      slider.value = "0";
      slider.dispatchEvent(new Event("input"));
      slider.dispatchEvent(new Event("change"));
    };
    let lastTap = 0;
    slider.addEventListener("pointerup", (e) => {
      const now = e.timeStamp;
      if (now - lastTap < 350) {
        lastTap = 0;
        reset();
      } else {
        lastTap = now;
      }
    });
    slider.addEventListener("dblclick", reset);
    this.bindNumber("strength", STRENGTH_RANGE, () => this.exposure.strength, (strength, persist) =>
      this.changeExposure({ strength }, persist));
    this.bindNumber("range", AE_RANGE, () => this.exposure.range, (range, persist) =>
      this.changeExposure({ range }, persist));
    this.exposureInput<HTMLSelectElement>("speed").addEventListener("change", (e) =>
      this.changeExposure({ speed: (e.target as HTMLSelectElement).value as ExposureSettings["speed"] }));
    this.exposureInput<HTMLSelectElement>("meter").addEventListener("change", (e) =>
      this.changeExposure({ meter: (e.target as HTMLSelectElement).value as ExposureSettings["meter"] }));
  }

  private numSlider(id: string): HTMLInputElement {
    return this.exposurePanel.querySelector(`[data-num="${id}"]`) as HTMLInputElement;
  }

  private numText(id: string): HTMLInputElement {
    return this.exposurePanel.querySelector(`[data-num-text="${id}"]`) as HTMLInputElement;
  }

  /** Shows a value in both the slider and the typed box. */
  private showNumber(id: string, range: NumericRange, value: number): void {
    this.numSlider(id).value = String(value);
    this.numText(id).value = formatValue(range, value);
  }

  /**
   * Slider and typed box for one number, kept in sync both ways. The slider applies live while
   * dragging and saves on release; typed text commits on Enter or blur: clamped to the range and
   * rounded to the step (shown back), or reverted to the last valid value when it is not a number.
   */
  private bindNumber(
    id: string,
    range: NumericRange,
    current: () => number,
    apply: (value: number, persist: boolean) => void,
  ): void {
    const slider = this.numSlider(id);
    const text = this.numText(id);
    slider.addEventListener("input", () => {
      const value = Number(slider.value);
      text.value = formatValue(range, value);
      apply(value, false);
    });
    slider.addEventListener("change", () => apply(Number(slider.value), true));
    const commit = (): void => {
      const value = commitTyped(range, text.value);
      this.showNumber(id, range, value ?? current());
      if (value !== undefined) apply(value, true);
    };
    text.addEventListener("change", commit);
    text.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
        text.blur();
      }
    });
  }

  private syncEffectPanel(): void {
    for (const [key, , range] of EFFECT_SLIDERS) this.showNumber(key, range, this.effects[key]);
    this.syncBlurButton();
  }

  /** The motion blur button shows on/off (aria-pressed); the slider keeps the strength. */
  private syncBlurButton(): void {
    this.blurButton?.setAttribute("aria-pressed", String(this.effects.motionBlurOn));
  }

  private changeEffect(patch: Partial<EffectSettings>, persist = true): void {
    this.effects = { ...this.effects, ...patch };
    if (persist) saveEffectSettings({ ...loadEffectSettings(), ...patch });
    applyEffectsToViewer(walkbakeOf(this.frame?.contentWindow), viewerPatch(patch, this.effects));
    if (patch.motionBlur !== undefined || patch.motionBlurOn !== undefined) {
      this.showNumber("motionBlur", EFFECT_RANGE, this.effects.motionBlur);
      this.syncBlurButton();
    }
  }

  private bindEffectPanel(): void {
    this.syncEffectPanel();
    for (const [key, , range] of EFFECT_SLIDERS) {
      this.bindNumber(key, range, () => this.effects[key], (value, persist) => {
        // The motion blur slider is its strength and turns it on above 0, off at 0.
        if (key !== "motionBlur") return this.changeEffect({ [key]: value }, persist);
        const { motionBlur, motionBlurOn } = setMotionBlurStrength(this.effects, value);
        this.changeEffect({ motionBlur, motionBlurOn }, persist);
      });
    }
  }

  private syncLookPanel(): void {
    for (const [key, , range] of LOOK_SLIDERS) this.showNumber(key, range, this.look[key]);
  }

  private changeLook(patch: Partial<LookSettings>, persist = true): void {
    this.look = { ...this.look, ...patch };
    if (persist) saveLookSettings({ ...loadLookSettings(), ...patch });
    applyLookToViewer(walkbakeOf(this.frame?.contentWindow), lookViewerPatch(patch, this.look));
  }

  private bindLookPanel(): void {
    this.syncLookPanel();
    for (const [key, , range] of LOOK_SLIDERS) {
      this.bindNumber(key, range, () => this.look[key], (value, persist) => this.changeLook({ [key]: value }, persist));
    }
    this.exposurePanel.querySelector("[data-look-reset]")?.addEventListener("click", () => {
      this.changeLook({ ...DEFAULT_LOOK_SETTINGS });
      this.syncLookPanel();
    });
  }

  private plantCheckbox(): HTMLInputElement {
    return this.exposurePanel.querySelector("[data-plant-lights]") as HTMLInputElement;
  }

  private syncPlantPanel(): void {
    this.plantCheckbox().checked = this.plants.lights;
    this.showNumber("lightIntensity", PLANT_LIGHT_RANGES.lightIntensity, this.plants.lightIntensity);
    for (const [key] of PLANT_SLIDERS) this.showNumber(key, PLANT_LIGHT_RANGES[key], this.plants[key]);
  }

  private changePlants(patch: Partial<PlantLightSettings>, persist = true): void {
    this.plants = { ...this.plants, ...patch };
    if (persist) savePlantLightSettings({ ...loadPlantLightSettings(), ...patch });
    applyPlantLightToViewer(walkbakeOf(this.frame?.contentWindow), patch);
  }

  private bindPlantPanel(): void {
    this.syncPlantPanel();
    this.plantCheckbox().addEventListener("change", (e) => this.changePlants({ lights: (e.target as HTMLInputElement).checked }));
    for (const key of ["lightIntensity", ...PLANT_SLIDERS.map(([k]) => k)] as PlantLightNumber[]) {
      this.bindNumber(key, PLANT_LIGHT_RANGES[key], () => this.plants[key], (value, persist) => this.changePlants({ [key]: value }, persist));
    }
    this.exposurePanel.querySelector("[data-plant-reset]")?.addEventListener("click", () => {
      this.changePlants({ ...DEFAULT_PLANT_LIGHT_SETTINGS });
      this.syncPlantPanel();
    });
  }

  private startExposurePanel(): void {
    this.syncExposurePanel();
    const refresh = (): void => {
      const value = walkbakeOf(this.frame?.contentWindow)?.aeValue;
      this.exposureOut("auto").textContent =
        value === null || value === undefined ? "n/a" : `${value >= 0 ? "+" : ""}${value.toFixed(2)} stops`;
    };
    refresh();
    this.stopAutoValuePolling();
    this.autoValueTimer = window.setInterval(refresh, 250);
  }

  private stopAutoValuePolling(): void {
    clearInterval(this.autoValueTimer);
    this.autoValueTimer = 0;
  }

  private readoutTimer = 0;

  /** Brief readout of the current focal length / zoom while pinching. */
  private showPinchReadout(text: string): void {
    const readout = this.element.querySelector(".pinch-readout") as HTMLElement;
    readout.textContent = text;
    readout.hidden = false;
    clearTimeout(this.readoutTimer);
    this.readoutTimer = window.setTimeout(() => (readout.hidden = true), 900);
  }

  get isKiosk(): boolean {
    return this.kioskSettings.kiosk;
  }

  /** Fullscreen button, kiosk mode, FPS box and wake lock: the tablet side of the Player. */
  private bindTablet(): void {
    this.kioskSettings = withKioskUrlOverrides(loadKioskSettings(), location.search);
    this.fsButton.addEventListener("click", () => {
      // The request has to start inside this click (user gesture).
      if (isFullscreen(document)) void exitFullscreen(document);
      else void enterFullscreen(this.element);
    });
    for (const type of FULLSCREEN_EVENTS) document.addEventListener(type, () => this.renderFullscreen());
    for (const input of this.panel.querySelectorAll<HTMLInputElement>("input[data-kiosk]")) {
      input.addEventListener("change", () => {
        if (input.dataset.kiosk === "kiosk") this.setKiosk(input.checked);
        else this.setFps(input.checked);
      });
    }
    this.bindKioskExit(this.element.querySelector(".kiosk-exit") as HTMLButtonElement);
    window.addEventListener("keydown", this.onFpsKey);
    this.element.addEventListener("pointermove", this.onPointerActivity);
    this.applyKiosk();
  }

  /** Press and hold (not a tap) leaves kiosk mode: a visitor cannot do it by accident. */
  private bindKioskExit(button: HTMLButtonElement): void {
    let timer = 0;
    const cancel = (): void => {
      clearTimeout(timer);
      button.classList.remove("pressing");
    };
    button.addEventListener("pointerdown", () => {
      button.classList.add("pressing");
      timer = window.setTimeout(() => {
        cancel();
        this.setKiosk(false);
      }, KIOSK_LEAVE_MS);
    });
    for (const type of ["pointerup", "pointerleave", "pointercancel"]) button.addEventListener(type, cancel);
    button.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private setKiosk(on: boolean): void {
    this.kioskSettings = { ...this.kioskSettings, kiosk: on };
    saveKioskSettings({ ...loadKioskSettings(), kiosk: on });
    this.applyKiosk();
  }

  private setFps(on: boolean): void {
    this.kioskSettings = { ...this.kioskSettings, fps: on };
    saveKioskSettings({ ...loadKioskSettings(), fps: on });
    this.syncTabletPanel();
    walkbakeOf(this.frame?.contentWindow)?.set({ fpsBox: on }); // live, in the open walk
  }

  private syncTabletPanel(): void {
    for (const input of this.panel.querySelectorAll<HTMLInputElement>("input[data-kiosk]")) {
      input.checked = this.kioskSettings[input.dataset.kiosk as keyof KioskSettings];
    }
  }

  private applyKiosk(): void {
    const on = this.kioskSettings.kiosk;
    this.element.classList.toggle("kiosk", on);
    document.documentElement.classList.toggle("kiosk", on);
    if (on) this.closePanels();
    this.frame?.contentWindow?.postMessage({ walkbakeKiosk: on }, location.origin); // the viewer folds or unfolds its bottom bar
    this.syncTabletPanel();
    this.renderFullscreen();
    this.updateWakeLock();
    if (on) this.armCursorIdle();
    else this.wakeCursor();
  }

  /** Fullscreen button: icon and label follow the state; hidden without an API or when already installed. */
  private renderFullscreen(): void {
    const full = isFullscreen(document);
    const available = showFullscreenButton(document, this.element, this.installed);
    // In kiosk mode the button only offers to enter: a visitor in fullscreen has nothing to undo.
    this.fsButton.hidden = !available || (this.kioskSettings.kiosk && full);
    const label = full ? "Exit full screen" : "Full screen";
    this.fsButton.innerHTML = `${full ? ICON_EXIT : ICON_ENTER}<span class="fs-label">${label}</span>`;
    this.fsButton.setAttribute("aria-label", label);
    this.fsButton.setAttribute("aria-pressed", String(full));
    this.fsButton.title = label;
  }

  /** Keeps the screen on while a walk is open or the device is in kiosk mode. */
  private updateWakeLock(): void {
    void this.wakeLock.setWanted(!this.element.hidden || this.kioskSettings.kiosk);
  }

  private readonly onFpsKey = (e: KeyboardEvent): void => {
    if (e.key.toLowerCase() !== "f" || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
    if (this.element.hidden) return;
    const target = e.target as HTMLElement | null;
    if (target && ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName)) return;
    this.setFps(!this.kioskSettings.fps);
  };

  /** Listeners on the viewer window (same origin): the F key, mouse activity, and no long-press menu in kiosk. */
  private watchFrameInput(win: Window): void {
    win.addEventListener("keydown", this.onFpsKey);
    win.addEventListener("pointermove", this.onPointerActivity);
    win.addEventListener("contextmenu", (e) => {
      if (this.kioskSettings.kiosk) e.preventDefault();
    });
  }

  private readonly onPointerActivity = (e: PointerEvent): void => {
    if (!this.kioskSettings.kiosk || e.pointerType !== "mouse") return;
    this.armCursorIdle();
  };

  /** Shows the cursor and hides it again after a few idle seconds (kiosk mode, mouse only). */
  private armCursorIdle(): void {
    this.wakeCursor();
    if (!this.kioskSettings.kiosk) return;
    this.cursorTimer = window.setTimeout(() => {
      this.element.classList.add("cursor-idle");
      try {
        const doc = this.frame?.contentDocument;
        if (!doc || this.cursorStyle) return;
        this.cursorStyle = doc.createElement("style");
        this.cursorStyle.textContent = "*{cursor:none !important}";
        doc.head.append(this.cursorStyle);
      } catch {
        // Viewer frame not reachable: only the shell's own cursor is hidden.
      }
    }, CURSOR_IDLE_MS);
  }

  private wakeCursor(): void {
    clearTimeout(this.cursorTimer);
    this.element.classList.remove("cursor-idle");
    this.cursorStyle?.remove();
    this.cursorStyle = null;
  }

  hide(): void {
    this.stopLoadWatch();
    if (!this.element.hidden && fullscreenElementOf(document) === this.element) void exitFullscreen(document);
    this.wakeCursor();
    this.uninstallTweaks?.();
    this.uninstallTweaks = null;
    if (this.frame) {
      // Blank the frame first so the viewer's WebGL context and textures are released.
      this.frame.src = "about:blank";
      this.frame.remove();
      this.frame = null;
    }
    this.element.hidden = true;
    this.renderFullscreen();
    this.updateWakeLock();
  }
}
